import { useId, type ReactNode } from 'react'

import { cn } from '../utilitarios/cn'

export interface PropriedadesCampo {
  rotulo?: ReactNode
  /** Marca com `*` e propaga `aria-required` ao controle. */
  obrigatorio?: boolean
  /** Texto de apoio abaixo do controle. Some quando há erro. */
  dica?: ReactNode
  erro?: string | null
  /** Conteúdo à direita do rótulo (contador de caracteres, link "buscar CEP"). */
  acessorio?: ReactNode
  className?: string
  /**
   * Recebe os atributos de acessibilidade já resolvidos. Render prop porque o
   * campo não sabe se o controle é `input`, `select`, `textarea` ou um composto —
   * e amarrar `aria-describedby` manualmente em cada tela é o tipo de detalhe que
   * se perde na terceira cópia.
   */
  children: (atributos: AtributosControle) => ReactNode
}

export interface AtributosControle {
  id: string
  'aria-invalid': boolean | undefined
  'aria-describedby': string | undefined
  'aria-required': boolean | undefined
  temErro: boolean
}

export function Campo({
  rotulo,
  obrigatorio,
  dica,
  erro,
  acessorio,
  className,
  children,
}: PropriedadesCampo) {
  const idBase = useId()
  const id = `campo-${idBase}`
  const idErro = `${id}-erro`
  const idDica = `${id}-dica`
  const temErro = Boolean(erro)

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {(rotulo || acessorio) && (
        <div className="flex items-baseline justify-between gap-3">
          {rotulo ? (
            <label htmlFor={id} className="text-sm font-medium text-conteudo">
              {rotulo}
              {obrigatorio && (
                <span className="ml-0.5 text-perigo" aria-hidden="true">
                  *
                </span>
              )}
            </label>
          ) : (
            <span />
          )}
          {acessorio ? <div className="text-xs text-conteudo-fraco">{acessorio}</div> : null}
        </div>
      )}

      {children({
        id,
        'aria-invalid': temErro || undefined,
        'aria-describedby': temErro ? idErro : dica ? idDica : undefined,
        'aria-required': obrigatorio || undefined,
        temErro,
      })}

      {temErro ? (
        <p id={idErro} role="alert" className="text-xs font-medium text-perigo">
          {erro}
        </p>
      ) : dica ? (
        <p id={idDica} className="text-xs text-conteudo-fraco">
          {dica}
        </p>
      ) : null}
    </div>
  )
}

/** Classes compartilhadas por `input`, `select` e `textarea`. */
export function classesControle(temErro?: boolean, className?: string): string {
  return cn(
    'w-full rounded-md border bg-superficie px-3 text-sm text-conteudo',
    'placeholder:text-conteudo-fraco',
    'transition-colors focus:outline-none focus:ring-2',
    'disabled:cursor-not-allowed disabled:bg-superficie-2 disabled:text-conteudo-fraco',
    temErro
      ? 'border-perigo focus:border-perigo focus:ring-perigo/25'
      : 'border-borda-forte focus:border-marca-500 focus:ring-marca-500/25',
    className,
  )
}
